import {
  calculateFormula,
  CoreConfig,
  evaluateValidator,
} from "@shared/core-utils";
import {
  CategoryData,
  ProductOptionsData,
} from "../components/ProductOption/ProductOption";
import { TValidator } from "@shared/types";

export type SubCategoryMap = {
  submap: Map<string, string[]>;
  selectedOptions: ProductOptionsData["OptionsList"];
};

/**
 * Evaluates validators with OR group support.
 * Validators with the same orGroup are ANDed together.
 * Different orGroups are ORed together.
 * If no orGroup is set, defaults to 0 (backward compatible - pure AND).
 */
export const evaluateValidatorsWithOrGroups = (
  validators: TValidator[],
  config: CoreConfig
): boolean => {
  if (!validators || validators.length === 0) return true;
  if (!config) return true;

  // Group validators by orGroup (default to 0)
  const groups = new Map<number, TValidator[]>();
  for (const v of validators) {
    const groupId = v.orGroup ?? 0;
    if (!groups.has(groupId)) {
      groups.set(groupId, []);
    }
    groups.get(groupId)!.push(v);
  }

  // If only one group (default behavior), use AND logic
  if (groups.size === 1) {
    return validators
      .map((v) =>
        evaluateValidator(v.Input ?? [], v.comparator, v.Output ?? [], config)
      )
      .reduce((a, b) => a && b, true);
  }

  // Multiple groups: AND within each group, OR between groups
  for (const [_, groupValidators] of groups) {
    const groupResult = groupValidators
      .map((v) =>
        evaluateValidator(v.Input ?? [], v.comparator, v.Output ?? [], config)
      )
      .reduce((a, b) => a && b, true);
    
    // If any group passes (all its validators are true), the whole thing passes
    if (groupResult) {
      return true;
    }
  }

  // No group passed
  return false;
};

export const validOptions = (
  selectedOptions: ProductOptionsData["OptionsList"],
  categorys: CategoryData[],
  config?: CoreConfig
): SubCategoryMap => {
  // Remove added options with no values
  const cleanSelectedOptions = selectedOptions?.filter(
    (so) => so.options.length > 0
  );

  const submap: SubCategoryMap["submap"] = new Map<string, string[]>(
    categorys
      .map((c) => {
        return c.SubCategorys.map((s) => [
          s._id,
          s.Options.filter((o) => {
            if (!o.validators || !config) return true;
            return evaluateValidatorsWithOrGroups(o.validators, config);
          }).map((o) => o._id),
        ]);
      })
      .reduce((pv, cv) => {
        return [...pv, ...cv];
      }, []) as any
  );

  // Build a map of all selected objects, and remove invalid options from array map
  if (cleanSelectedOptions) {
    for (const option of cleanSelectedOptions) {
      for (const selected of option.options) {
        // const al = selectedOptions.find( so => so.options.includes( option.))
        const OptionObject = option?.SubCategory?.Options?.find(
          (o) => o._id === selected
        );
        if (!OptionObject) {
          continue;
        }
        for (const al of OptionObject.AllowList) {
          const cat = submap.get(al.subcategory);
          submap.set(
            al.subcategory,
            (cat ?? []).filter((c) => al.options?.includes(c))
          );
        }
      }
    }
  }

  // Do the reverse lookup, see if any options are invalid with our selected options
  for (const category of categorys) {
    for (const subcategory of category.SubCategorys) {
      const currentValidOptions = submap.get(subcategory._id) ?? [];
      let changed = false;
      for (const option of subcategory.Options) {
        if (currentValidOptions.includes(option._id)) {
          for (const selectedOption of cleanSelectedOptions ?? []) {
            const restricted = option.AllowList.find(
              (al) => al.subcategory === selectedOption.SubCategory?._id
            );
            if (restricted && !restricted.options.includes(option._id)) {
              changed = true;
              currentValidOptions.splice(
                currentValidOptions.indexOf(option._id),
                1
              );
            }
          }
        }
      }
      if (changed) {
        submap.set(subcategory._id, currentValidOptions);
      }
    }
  }

  return { submap, selectedOptions };
};

export const isParentCompatible = ({
  parentCategorys,
  category,
}: {
  parentCategorys?: string[];
  category: {
    useParentValues?: boolean;
    _id: string;
    parentProductCategory?: string;
  } & Record<string, any>;
}) => {
  if (!category?.useParentValues) {
    return true;
  }
  const parentId = category?.parentProductCategory;
  return Boolean(parentId && parentCategorys?.includes(parentId));
};

const log = (message: string) => {
  // @ts-ignore
  //window.lastLog = message;
  //console.log(` ============ ${message} =============`);
};

export const isCompatibleCategory = (
  subcatmap: SubCategoryMap,
  categoryId: string,
  categorys: CategoryData[],
  checkedIds: string[] = []
) => {
  const Category = categorys.find((c) => c._id === categoryId);
  if (Category && Category.requiredCategorys) {
    const requirementsValid = Category.requiredCategorys
      .map((c) => {
        if (checkedIds.includes(c)) {
          return true;
        }
        return isCompatibleCategory(subcatmap, c, categorys, [
          ...checkedIds,
          categoryId,
        ]);
      })
      .reduce((pv, cv) => pv && cv, true);
    if (requirementsValid === false) {
      log(`Requirements invalid`);
      return false;
    }
  }

  // check incompatible
  const selectedCategories = (subcatmap.selectedOptions ?? []).reduce<
    string[]
  >((pv, so) => {
    const catId = so.SubCategory?.Category._id;
    if (!catId || pv.includes(catId)) {
      return pv;
    }
    return [catId, ...pv];
  }, []);

  const selectedIncompatible = categorys
    .filter((c) => selectedCategories.includes(c._id))
    .reduce<string[]>((pv, cv) => [...pv, ...cv.incompatibleCategorys], []);
  if (selectedIncompatible.includes(categoryId)) {
    log(`selectedIncompatible`);
    return false;
  }
  const checkIdIncompatibilities =
    categorys.find((c) => c._id === categoryId)?.incompatibleCategorys || [];
  for (const cid of checkIdIncompatibilities) {
    if (selectedCategories.includes(cid)) {
      log(`selectedCategories includes ${cid}`);
      return false;
    }
  }

  const getAvailableOptions = (
    subcatmap: SubCategoryMap,
    subCategoryId: string
  ): string[] => {
    const submap = subcatmap.submap;
    if (submap && typeof submap.get === "function") {
      return submap.get(subCategoryId) || [];
    } else if (
      submap &&
      Object.prototype.hasOwnProperty.call(submap, subCategoryId)
    ) {
      return (
        (submap as unknown as Record<string, string[] | undefined>)[
          subCategoryId
        ] || []
      );
    }
    return [];
  };
  const categoryMapReason = categorys
    .find((c) => c._id === categoryId)
    ?.SubCategorys.map(
      (s) => getAvailableOptions(subcatmap, s._id).length >= 1
    );
  const valid = (categoryMapReason || []).reduce((pv, cv) => pv && cv, true);
  return valid;
};

export const isCompatibleOption = (
  catmap: SubCategoryMap,
  { subCategoryId, optionId }: { subCategoryId: string; optionId: string }
) => {
  const validOptions = catmap.submap.get(subCategoryId) || [];
  return validOptions.includes(optionId);
};
